import { CredentialProvider, FormState } from "./types"

/** Suffix under `settings.liveChat.validation.` naming the message to show. */
export type ValidationErrorKey = "whitespace" | "invalidUrl" | "insecureUrl" | "invalidPadding"

export type FormErrors = Partial<Record<keyof FormState, ValidationErrorKey>>

/** Identifiers that travel in a URL or a script attribute: any whitespace is a paste error. */
const IDENTIFIER_FIELDS: (keyof FormState)[] = [
  "intercomAppId",
  "tawkPropertyId",
  "tawkWidgetId",
  "inhousePublicKey"
]

const PADDING_FIELDS: (keyof FormState)[] = [
  "horizontalPadding",
  "sportsbookHorizontalPadding",
  "verticalPadding"
]

/** Public identifiers per provider — the values the widget cannot load without. */
const PROVIDER_IDENTIFIERS: Record<CredentialProvider, (keyof FormState)[]> = {
  intercom: ["intercomAppId"],
  tawk: ["tawkPropertyId", "tawkWidgetId"],
  inhouse: ["inhousePublicKey"]
}

const validateIdentifier = (value: string): ValidationErrorKey | undefined =>
  /\s/.test(value) ? "whitespace" : undefined

const validateHost = (value: string): ValidationErrorKey | undefined => {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return "invalidUrl"
  }
  // The embed injects a script into the casino page; an http origin would be blocked as mixed content.
  return url.protocol === "https:" ? undefined : "insecureUrl"
}

const validatePadding = (value: string): ValidationErrorKey | undefined =>
  /^\d+$/.test(value.trim()) ? undefined : "invalidPadding"

/** Only the selected provider is checked; the others are off screen and pass through. */
export function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {}

  const provider = form.provider
  const isCredentialProvider = provider !== "" && provider !== "none"

  if (isCredentialProvider) {
    IDENTIFIER_FIELDS.filter((field) => PROVIDER_IDENTIFIERS[provider].includes(field)).forEach((field) => {
      const value = form[field] as string
      if (!value) return
      const error = validateIdentifier(value)
      if (error) errors[field] = error
    })

    if (provider === "inhouse" && form.inhouseHost) {
      const error = validateHost(form.inhouseHost)
      if (error) errors.inhouseHost = error
    }
  }

  PADDING_FIELDS.forEach((field) => {
    const value = form[field] as string
    if (!value.trim()) return
    const error = validatePadding(value)
    if (error) errors[field] = error
  })

  return errors
}

/** The provider will fall back to the frontend env for its identifiers. Not an error. */
export function reliesOnEnvironment(form: FormState): boolean {
  const provider = form.provider
  if (provider === "" || provider === "none") return false

  return PROVIDER_IDENTIFIERS[provider].some((field) => !(form[field] as string).trim())
}

export const hasErrors = (errors: FormErrors): boolean => Object.keys(errors).length > 0
